import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listCampaigns,
  createCampaign,
  updateCampaign,
  startCampaign,
  stopCampaign,
  deleteCampaign,
  duplicateCampaign,
  resetCampaignList,
  campaignActionErrorMessage,
  type Campaign,
  type CampaignDraftProposal,
  type CampaignInput,
} from "../api/campaigns";
import CampaignAiBuilderModal from "../components/CampaignAiBuilderModal";
import { listGroups } from "../api/contacts";
import {
  listDidGroups,
  getCampaignExpansionProposals,
  approveCampaignExpansion,
  type CampaignExpansionProposal,
} from "../api/dids";
import { listRecordings } from "../api/recordings";
import { ReplaceCampaignAudio } from "../components/ReplaceCampaignAudio";
import { listExtensions } from "../api/extensions";
import { getBreakdown } from "../api/lnp";
import { CampaignDashboard } from "./CampaignDashboard";
import { DialPacingPanel } from "../components/DialPacingPanel";
import { QueueMembersEditor } from "../components/QueueMembersEditor";
import { LeadFormEditor } from "../components/LeadFormEditor";
import { listQueueMembers } from "../api/queues";

type LnpFilterMode = "off" | "exclude" | "include";

const SERVICE_LABELS: Record<number, string> = { 1: "Voice", 2: "Fax", 4: "SMS", 8: "Email" };
function serviceLabel(flag: number): string {
  const parts = Object.entries(SERVICE_LABELS)
    .filter(([bit]) => (flag & Number(bit)) !== 0)
    .map(([, label]) => label);
  return parts.length ? parts.join(", ") : `#${flag}`;
}

function statusBadge(status: string): string {
  const map: Record<string, string> = {
    running: "bg-green-100 text-green-800",
    active: "bg-green-100 text-green-800",
    starting: "bg-green-100 text-green-800",
    completed: "bg-gray-100 text-gray-700",
    stopped: "bg-yellow-100 text-yellow-800",
    paused: "bg-yellow-100 text-yellow-800",
    pending: "bg-blue-100 text-blue-800",
    scheduled: "bg-blue-100 text-blue-800",
  };
  return map[status] || "bg-gray-100 text-gray-700";
}

const RUNNING_STATES = new Set(["running", "active", "starting"]);
const STARTABLE = new Set(["pending", "stopped", "scheduled", "paused"]);

const DIAL_METHODS: { value: number; label: string }[] = [
  { value: 36, label: "Automatic (broadcast)" },
  { value: 100, label: "Predictive" },
  { value: 164, label: "Power" },
  { value: 420, label: "Progressive" },
  { value: 676, label: "Preview" },
];

function CampaignEditor({
  campaign,
  initialDraft,
  onClose,
  onSaved,
  onSwitchToAiBuilder,
}: {
  campaign: Campaign | null;
  initialDraft?: CampaignDraftProposal | null;
  onClose: () => void;
  onSaved: () => void;
  onSwitchToAiBuilder?: () => void;
}) {
  // Only approved (active) lists are selectable — a list being uploaded or
  // pending DNC-approval must not be dialable yet.
  const { data: groups } = useQuery({
    queryKey: ["contact-groups", "active"],
    queryFn: () => listGroups("active"),
  });
  const { data: didGroups } = useQuery({ queryKey: ["did-groups"], queryFn: listDidGroups });
  const { data: recordings } = useQuery({ queryKey: ["recordings"], queryFn: listRecordings });
  const { data: extResp } = useQuery({
    queryKey: ["extensions-all"],
    queryFn: () => listExtensions({ per_page: 200 }),
  });
  const extensions = extResp?.data ?? [];
  const [name, setName] = useState(campaign?.name ?? initialDraft?.name ?? "");
  const [service, setService] = useState(campaign?.service_flag ?? 1);
  const [dialMethod, setDialMethod] = useState(campaign?.dial_method ?? 36);
  const [channel, setChannel] = useState(campaign?.channel ?? initialDraft?.channel ?? 1);
  // Concurrency is only sent when the operator actually edits it, gated on
  // "touched" exactly like transfer_target below. Automatic dial pacing
  // (DialPacingPanel) moves campaign.channel on its own; this form loads the
  // cap once, so sending it back on every save would silently restore the
  // pre-pacing number the moment an unrelated field was changed -- and the
  // loop would then lower it all over again (a flapping cap). PUT /settings
  // only writes fields it is given.
  const [channelTouched, setChannelTouched] = useState(Boolean(initialDraft?.channel));
  const [ringSec, setRingSec] = useState(
    campaign ? Math.round((campaign.ring_time ?? 30000) / 1000) : (initialDraft?.ring_time_sec ?? 30),
  );
  const [tries, setTries] = useState(campaign?.try_allowed ?? initialDraft?.try_allowed ?? 1);
  const [groupId, setGroupId] = useState<number | null>(
    campaign?.contact_group_id ?? initialDraft?.contact_group_id ?? null,
  );
  // "Touched" like channel and transfer_target below, and for a sharper reason:
  // the update route re-feeds the spool from the list whenever it receives a
  // contact_group_id (app/routers/campaign.py, `if updates.get(...)`), and that
  // feed's duplicate check is PER CAMPAIGN. A campaign that shares a list with
  // another one — the two Tax lanes share group 140, split by moving spool rows
  // — would have every one of the other lane's ~128,000 numbers inserted as
  // pending and dialled a second time. Duplicate calls to a real person are a
  // TCPA problem, not a billing one, and it would fire on a save that only
  // changed the campaign's name.
  const [groupTouched, setGroupTouched] = useState(Boolean(initialDraft?.contact_group_id));
  // Policy: every campaign leaves a voicemail on a detected answering machine.
  // The old "Hang up" option was removed — am_action is fixed to leave-message.
  const [amAction] = useState("leavemessage");
  // Caller ID = always rotate a DID group (random, local-presence, reputation/volume aware).
  const [didGroupId, setDidGroupId] = useState<number | null>(
    campaign?.did_group_id ?? initialDraft?.did_group_id ?? null,
  );
  const [recordingFile, setRecordingFile] = useState(
    campaign?.recording_file ?? initialDraft?.recording_file ?? "",
  );
  // Campaign type: voice broadcast (0), press-1 Outbound IVR (1), or direct connect + VM drop (2).
  const [campaignType, setCampaignType] = useState<string>(
    campaign
      ? campaign.message_type === 2
        ? "direct"
        : campaign.message_type === 1
          ? "press1"
          : "broadcast"
      : (initialDraft?.campaign_type ?? "press1"),
  );
  const [typeTouched, setTypeTouched] = useState(Boolean(initialDraft?.campaign_type));
  const [transferExtensionId, setTransferExtensionId] = useState<number | null>(
    campaign?.transfer_extension_id ?? initialDraft?.transfer_extension_id ?? null,
  );
  const [transferExtTouched, setTransferExtTouched] = useState(Boolean(initialDraft?.transfer_extension_id));
  const [amRecordingFile, setAmRecordingFile] = useState(
    campaign?.am_recording_file ?? initialDraft?.am_recording_file ?? "",
  );
  const [connectKey, setConnectKey] = useState(
    campaign?.connect_key ?? initialDraft?.connect_key ?? "1",
  );
  const [optoutKey, setOptoutKey] = useState(
    campaign?.optout_key ?? initialDraft?.optout_key ?? "9",
  );
  // Where press-1 connects the caller: a phone number (the extension's PSTN
  // forward, below) or this campaign's agent queue. Defaults to "pstn" so an
  // untouched campaign keeps dialing the extension exactly as before this
  // control existed. Gated on "touched" the same way the LNP filter below is
  // — PUT /settings only writes fields it's given, so saving an unrelated
  // field must not silently reset an "agents" campaign back to "pstn".
  const [transferTarget, setTransferTarget] = useState<"pstn" | "agents">(
    campaign?.transfer_target === "agents" ? "agents" : "pstn",
  );
  const [transferTargetTouched, setTransferTargetTouched] = useState(false);
  // Carrier (LNP) filter — pre-filled from the campaign's round-tripped lnp_*
  // fields (CampaignResponse) when editing an existing campaign, so the editor
  // reflects the filter that's actually active instead of always showing "off".
  const initialLnpMode: LnpFilterMode =
    campaign?.lnp_filter_mode === "include" || campaign?.lnp_filter_mode === "exclude"
      ? campaign.lnp_filter_mode
      : "off";
  const [lnpFilterMode, setLnpFilterMode] = useState<LnpFilterMode>(initialLnpMode);
  const [lnpFilterCarriers, setLnpFilterCarriers] = useState<Set<string>>(
    new Set((campaign?.lnp_filter_carriers ?? "").split(",").map((s) => s.trim()).filter(Boolean)),
  );
  const [lnpRequireMobile, setLnpRequireMobile] = useState(campaign?.lnp_require_mobile === "1");
  const [lnpRequireValid, setLnpRequireValid] = useState(campaign?.lnp_require_valid === "1");
  // The carrier-filter section is pre-filled above, but PUT /settings only
  // writes the fields it's given — an untouched section re-sending the same
  // prefilled values on every unrelated save is harmless, but we still gate on
  // "touched" so a save doesn't clobber a filter set from lnp_unknown_policy or
  // similar edits made outside this UI. Only send the lnp_* fields on update
  // when the operator actually touched this section this session.
  const [lnpTouched, setLnpTouched] = useState(false);
  const lnpFilterActive = lnpFilterMode !== "off";
  // Voicemail skip. Off unless the operator turns it on — this never engages by
  // itself, and an existing campaign opens showing whatever is actually set.
  const [vmSkip, setVmSkip] = useState(campaign?.vm_skip_mode === "on");
  const [vmMinCalls, setVmMinCalls] = useState(campaign?.vm_skip_min_calls || "4");
  const [vmTouched, setVmTouched] = useState(false);
  // Carrier options for the multi-select come from the selected list's own
  // breakdown — only fetched once a list is chosen and the filter is in use.
  const { data: lnpBreakdown } = useQuery({
    queryKey: ["lnp-breakdown", groupId],
    queryFn: () => getBreakdown(groupId as number),
    enabled: groupId !== null && lnpFilterActive,
  });
  const toggleCarrier = (spid: string) => {
    setLnpTouched(true);
    setLnpFilterCarriers((s) => {
      const n = new Set(s);
      n.has(spid) ? n.delete(spid) : n.add(spid);
      return n;
    });
  };

  const isPress1 = campaignType === "press1";
  const isDirect = campaignType === "direct";
  const hasTransfer = isPress1 || isDirect;
  // Same query key QueueMembersEditor uses internally — TanStack Query
  // dedupes/shares the cache entry, so this doesn't add a second fetch when
  // this campaign IS press-1/direct. Needed here (not just inside
  // QueueMembersEditor) so the "agents mode with no assigned agent" trap can
  // be warned about right where the routing radio lives: agents-mode + zero
  // members means Queue() targets a queue 0039 excludes as memberless, the
  // next dialplan priority is Hangup(), and the caller who just pressed 1
  // gets silently hung up on.
  //
  // Gated on `hasTransfer` (not just `!!campaign`): a broadcast campaign has no
  // transfer-queue UI anywhere on this form, so fetching its queue
  // membership on every edit-panel open was a pure wasted GET — this hook
  // is declared here, below `hasTransfer`, specifically so it can read it.
  const { data: queueMembers, isLoading: queueMembersLoading } = useQuery({
    queryKey: ["queue-members", campaign?.campaign_id],
    queryFn: () => listQueueMembers(campaign!.campaign_id),
    enabled: !!campaign && hasTransfer,
  });
  const isBroadcast = campaignType === "broadcast";
  const leaveMessage = amAction !== "hangup";
  // Broadcast and Press-1 need a primary human message recording; Direct Connect
  // bridges live humans immediately and only requires an answering-machine voicemail.
  const recordingNeeded = isPress1 || isBroadcast;
  const recordingMissing = recordingNeeded && !recordingFile;
  const amRecordingMissing = isDirect && leaveMessage && !amRecordingFile;
  // The extension/PSTN-forward requirement only applies when routing to a
  // phone number — a campaign sending transfers to the agent queue has no use
  // for a connect-to-agent extension.
  const transferToPstn = transferTarget === "pstn";
  const transferMissing = hasTransfer && transferToPstn && !transferExtensionId;
  // Selected extension's PSTN forward must be set, else the dialplan silently
  // falls through to a Queue named after the campaign that almost never exists.
  const selectedExt = extensions.find((x) => x.extension_id === transferExtensionId);
  const transferNoForward = hasTransfer && transferToPstn && !!transferExtensionId && !selectedExt?.forward_to;
  // agents-mode caller trap: a queue with zero VALID members is excluded from
  // the realtime queue mapping, so Queue() targets a queue Asterisk treats as
  // nonexistent and falls straight to the dialplan's next priority —
  // Hangup(). A caller who just pressed 1 gets silently disconnected. This
  // is a warning, not a hard Save block: an operator may deliberately save
  // "agents" first and assign members immediately after (QueueMembersEditor
  // syncs independently of Save).
  //
  // Counts `valid` members, not raw row count (beads-9oa): a queue_member
  // row survives its extension being soft-deleted or given a forward_to
  // later, but Asterisk's `members` view (0017/0042) would no longer
  // resolve it — a raw count could read "1 member" while the queue is
  // actually memberless to Asterisk.
  const validMemberCount = (queueMembers ?? []).filter((m) => m.valid).length;
  const membersEmpty =
    hasTransfer && !transferToPstn && !!campaign && !queueMembersLoading && validMemberCount === 0;
  // The dialplan checks CONNECT_KEY first, so picking the same digit for both
  // means every press transfers to the agent and opt-out can never fire.
  const keysCollide = isPress1 && connectKey === optoutKey;

  const save = useMutation({
    mutationFn: () => {
      // Creating a new campaign always sends the carrier-filter fields (there's
      // nothing to clobber yet). Updating an existing campaign only sends them
      // if the operator actually touched the carrier-filter section this
      // session — the GET doesn't echo lnp_* back, so an untouched section is
      // just stale "off"/empty UI state, not the operator's intent, and PUTting
      // it would silently reset a filter that's already configured server-side.
      const lnpFields: Partial<CampaignInput> =
        !campaign || lnpTouched
          ? {
              lnp_filter_mode: lnpFilterMode,
              lnp_filter_carriers: [...lnpFilterCarriers].join(","),
              lnp_require_mobile: lnpRequireMobile ? "1" : "0",
              lnp_require_valid: lnpRequireValid ? "1" : "0",
            }
          : {};
      // Same "touched" gating, same reason: an untouched toggle must not be
      // sent on an unrelated save, or it would silently switch the skip off
      // for a campaign that had it on.
      const vmFields: Partial<CampaignInput> =
        !campaign || vmTouched
          ? { vm_skip_mode: vmSkip ? "on" : "off", vm_skip_min_calls: vmMinCalls }
          : {};
      // Same "touched" gating as the LNP filter above, and for the same
      // reason: PUT /settings only writes fields it's given, so an untouched
      // radio must not be sent on every unrelated save — that would silently
      // reset an "agents" campaign's routing back to "pstn".
      const transferTargetFields: Partial<CampaignInput> =
        !campaign || transferTargetTouched ? { transfer_target: transferTarget } : {};
      const channelFields: Partial<CampaignInput> =
        !campaign || channelTouched ? { channel } : {};
      const typeFields: Partial<CampaignInput> =
        !campaign || typeTouched
          ? {
              message_type: isDirect ? 2 : isPress1 ? 1 : 0,
              ...(typeTouched ? { type_touched: true } : {}),
            }
          : {};
      const transferExtFields: Partial<CampaignInput> =
        !campaign || transferExtTouched || typeTouched
          ? { transfer_extension_id: hasTransfer ? transferExtensionId : null }
          : {};
      const body: CampaignInput = {
        name,
        service_flag: service,
        dial_method: dialMethod,
        ...channelFields,
        ring_time: ringSec * 1000,
        // Only when the operator actually chose a list this session; on create
        // it is always sent, because a new campaign has no spool to protect.
        ...(groupTouched || !campaign ? { contact_group_id: groupId } : {}),
        try_allowed: tries,
        callerid: null,
        callerid_name: campaign?.callerid_name ?? null,
        // Caller ID is always a DID-group rotation: random + local-presence,
        // automatically skipping quarantined numbers and respecting volume caps.
        callerid_mode: "group",
        did_group_id: didGroupId,
        callerid_random: true,
        local_dial: true,
        recording_file: recordingNeeded ? recordingFile || null : null,
        am_recording_file:
          hasTransfer || (isBroadcast && leaveMessage) ? amRecordingFile || null : null,
        ...typeFields,
        // Deliberately still sent even in "agents" mode (not gated on
        // transferToPstn, unlike the pstn-only validation above): keeping the
        // extension on file makes switching back to "a phone number" later
        // revert-friendly (the picker stays pre-filled) rather than forcing
        // the operator to re-choose it. Harmless while in agents mode —
        // build_transfer_dialstring() (campaign_runner.py) short-circuits on
        // transfer_target=agents before it ever reads transfer_number, so a
        // stored-but-unused extension_id has no effect on the dial path.
        ...transferExtFields,
        connect_key: isPress1 ? connectKey : null,
        optout_key: isPress1 ? optoutKey : null,
        am_action: amAction || null,
        ...lnpFields,
        ...vmFields,
        ...transferTargetFields,
      };
      return campaign
        ? updateCampaign(campaign.campaign_id, body)
        : createCampaign(body).then(() => undefined);
    },
    onSuccess: onSaved,
  });

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-10 p-4">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-full overflow-auto space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">{campaign ? "Edit" : "New"} campaign</h2>
          {!campaign && onSwitchToAiBuilder && (
            <button
              type="button"
              onClick={onSwitchToAiBuilder}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-full px-3 py-1"
            >
              ✨ Build with Plain English
            </button>
          )}
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Name</label>
          <input className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Channel</label>
            <select className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={service} onChange={(e) => setService(Number(e.target.value))}>
              <option value={1}>Voice</option>
              <option value={8}>Email</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Dial method</label>
            <select className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={dialMethod} onChange={(e) => setDialMethod(Number(e.target.value))}>
              {DIAL_METHODS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Contact list</label>
          <select className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={groupId ?? ""} onChange={(e) => { setGroupTouched(true); setGroupId(e.target.value ? Number(e.target.value) : null); }}>
            <option value="">— none —</option>
            {(groups ?? []).map((g) => (
              <option key={g.contact_group_id} value={g.contact_group_id}>
                {g.name} ({g.contact_count.toLocaleString()})
              </option>
            ))}
          </select>
        </div>
        <div className="border-t border-gray-100 pt-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Carrier filter</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={lnpFilterMode}
            onChange={(e) => { setLnpFilterMode(e.target.value as LnpFilterMode); setLnpTouched(true); }}
          >
            <option value="off">Off — dial everyone</option>
            <option value="exclude">Exclude selected carriers</option>
            <option value="include">Only dial selected carriers</option>
          </select>
          <p className="text-[11px] text-gray-400 mt-1">
            Applies when the campaign is created and its list is loaded. Changing this filter on
            an existing campaign does not re-load contacts already queued to dial.
          </p>
          {lnpFilterActive && (
            <div className="mt-2 space-y-2">
              {!groupId ? (
                <p className="text-xs text-amber-600">Pick a contact list above to choose carriers.</p>
              ) : (lnpBreakdown?.by_carrier ?? []).length === 0 ? (
                <p className="text-xs text-amber-600">
                  No carrier data for this list yet — run "Look up carriers" on it from Contact
                  Lists first.
                </p>
              ) : (
                <div className="border border-gray-200 rounded max-h-40 overflow-auto divide-y divide-gray-100">
                  {(lnpBreakdown?.by_carrier ?? []).map((c) => (
                    <label key={c.spid} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={lnpFilterCarriers.has(c.spid)}
                        onChange={() => toggleCarrier(c.spid)}
                      />
                      <span>{c.carrier || c.spid || "Unknown"}</span>
                      <span className="text-gray-400 text-xs">({c.count.toLocaleString()})</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={lnpRequireMobile}
                    onChange={(e) => { setLnpRequireMobile(e.target.checked); setLnpTouched(true); }}
                  />
                  Mobile only
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={lnpRequireValid}
                    onChange={(e) => { setLnpRequireValid(e.target.checked); setLnpTouched(true); }}
                  />
                  Valid only
                </label>
              </div>
              <p className="text-[11px] text-gray-400">
                Numbers not yet looked up are dialed as normal unless "Only dial selected
                carriers", "Mobile only", or "Valid only" is on.
              </p>
            </div>
          )}
        </div>
        <div className="border-t border-gray-100 pt-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-600">
            <input
              type="checkbox"
              checked={vmSkip}
              onChange={(e) => { setVmSkip(e.target.checked); setVmTouched(true); }}
            />
            Skip numbers that always reach voicemail
          </label>
          <p className="text-[11px] text-gray-400 mt-1">
            Leaves out numbers that have answered several times, sat through the entire
            message on every single call, and have never once pressed a key. Off unless you
            turn it on.
          </p>
          {vmSkip && (
            <div className="mt-2">
              <label className="block text-xs text-gray-500 mb-1">
                How sure do you want to be?
              </label>
              <select
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                value={vmMinCalls}
                onChange={(e) => { setVmMinCalls(e.target.value); setVmTouched(true); }}
              >
                <option value="3">Skip after 3 such calls — skips the most numbers</option>
                <option value="4">Skip after 4 such calls — recommended</option>
                <option value="5">Skip after 5 such calls — most cautious</option>
              </select>
              <p className="text-[11px] text-gray-400 mt-1">
                Applies when the list is loaded. Numbers we have actually recorded and
                confirmed as voicemail are skipped regardless of this setting. A number that
                later answers and presses a key returns to normal dialing on its own.
              </p>
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Concurrency</label>
            <input type="number" min={1} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={channel} onChange={(e) => { setChannel(Number(e.target.value)); setChannelTouched(true); }} />
            {campaign?.pacing_enabled && (
              <p className="text-[11px] text-blue-800 mt-1">
                Automatic pacing is adjusting this cap. Editing it here sets the cap by hand and restarts pacing from your number; leave it to keep the current cap.
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Ring (sec)</label>
            <input type="number" min={5} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={ringSec} onChange={(e) => setRingSec(Number(e.target.value))} />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tries</label>
            <input type="number" min={1} className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={tries} onChange={(e) => setTries(Number(e.target.value))} />
          </div>
        </div>
        <div className="border-t border-gray-100 pt-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Campaign type</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={campaignType}
            onChange={(e) => { setCampaignType(e.target.value); setTypeTouched(true); }}
          >
            <option value="broadcast">Voice broadcast</option>
            <option value="press1">Press-1 transfer (Outbound IVR)</option>
            <option value="direct">Direct connect + Voicemail drop</option>
          </select>
          {hasTransfer && (
            <div className="mt-2 space-y-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {isDirect ? "Connect live pickups to" : "Send transfers to"}
                </label>
                <div className="flex gap-4 text-sm mb-2">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="transfer_target"
                      checked={transferTarget === "pstn"}
                      onChange={() => { setTransferTarget("pstn"); setTransferTargetTouched(true); }}
                    />
                    a phone number
                  </label>
                  {/* Disabled until the campaign exists: agent assignment
                      (QueueMembersEditor) needs a campaign_id, and there's
                      nowhere to assign an agent to yet on a brand-new campaign. */}
                  <label
                    className={`flex items-center gap-1.5 ${campaign ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                  >
                    <input
                      type="radio"
                      name="transfer_target"
                      checked={transferTarget === "agents"}
                      disabled={!campaign}
                      onChange={() => { setTransferTarget("agents"); setTransferTargetTouched(true); }}
                    />
                    my agents
                  </label>
                </div>
                {!campaign && (
                  <p className="text-[11px] text-gray-400 mb-2">
                    Save the campaign first, then come back to route transfers to your agents.
                  </p>
                )}
                {transferToPstn && (
                  <>
                    <label className="block text-xs text-gray-500 mb-1">Connect-to-agent extension</label>
                    <select
                      className={`w-full border rounded px-3 py-2 text-sm ${transferMissing ? "border-red-400" : "border-gray-300"}`}
                      value={transferExtensionId ?? ""}
                      onChange={(e) => { setTransferExtensionId(e.target.value ? Number(e.target.value) : null); setTransferExtTouched(true); }}
                    >
                      <option value="">— select an extension —</option>
                      {extensions.map((x) => (
                        <option key={x.extension_id} value={x.extension_id} disabled={!x.forward_to}>
                          {x.name}{x.forward_to ? ` → ${x.forward_to}` : " (no PSTN forward set)"}
                        </option>
                      ))}
                    </select>
                    {transferMissing && (
                      <p className="text-xs text-red-600 mt-1">
                        {isDirect
                          ? "Choose the extension live pickups connect to."
                          : "Choose the extension the connect key transfers to."}
                      </p>
                    )}
                    {transferNoForward && (
                      <p className="text-xs text-red-600 mt-1">This extension has no PSTN forward set — transfers would silently fail. Pick another or set its forward number.</p>
                    )}
                    <p className="text-[11px] text-gray-400 mt-1">
                      {isDirect
                        ? "When a live human answers, the call immediately bridges to this extension's outside (PSTN) number; voicemails receive the voicemail drop below."
                        : "Pressing the connect key transfers the recipient to this extension's outside (PSTN) number. Uses Whisper answering-machine detection."}
                    </p>
                  </>
                )}
                {!transferToPstn && (
                  <>
                    <p className="text-[11px] text-gray-400 mb-1">
                      {isDirect
                        ? "When a live human answers, the call immediately routes into this campaign's agent queue; voicemails receive the voicemail drop below."
                        : "Pressing the connect key routes the recipient into this campaign's agent queue instead of dialing an outside number."}
                    </p>
                    {membersEmpty && (
                      <p className="text-xs font-medium text-red-600 mb-1">
                        Transfers will fail — assign at least one agent below.
                      </p>
                    )}
                  </>
                )}
                {campaign && <QueueMembersEditor campaignId={campaign.campaign_id} />}
              </div>
              {isPress1 && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Connect-to-agent key</label>
                      <select className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={connectKey} onChange={(e) => setConnectKey(e.target.value)}>
                        {["1","2","3","4","5","6","7","8","9","0"].map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Opt-out (DNC) key</label>
                      <select className="w-full border border-gray-300 rounded px-3 py-2 text-sm" value={optoutKey} onChange={(e) => setOptoutKey(e.target.value)}>
                        {["1","2","3","4","5","6","7","8","9","0"].map((k) => <option key={k} value={k}>{k}</option>)}
                      </select>
                    </div>
                  </div>
                  {keysCollide && (
                    <p className="text-xs text-red-600 mt-1">Connect key and opt-out key must be different — if they match, every press transfers to the agent and opt-out never fires.</p>
                  )}
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">IVR message (played to humans)</label>
                    <select
                      className={`w-full border rounded px-3 py-2 text-sm ${recordingMissing ? "border-red-400" : "border-gray-300"}`}
                      value={recordingFile}
                      onChange={(e) => setRecordingFile(e.target.value)}
                    >
                      <option value="">— select a recording —</option>
                      {(recordings ?? []).map((r) => (
                        <option key={r.filename} value={r.ref}>{r.nickname || r.filename}</option>
                      ))}
                    </select>
                    {recordingMissing && (
                      <p className="text-xs text-red-600 mt-1">Choose the IVR message recording to play.</p>
                    )}
                    {campaign && (
                      <ReplaceCampaignAudio
                        campaignId={campaign.campaign_id}
                        slot="greeting"
                        label="IVR message"
                        isRunning={campaign.status === "running"}
                        onSwapped={(ref) => setRecordingFile(ref)}
                      />
                    )}
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  {isDirect ? "Voicemail drop recording (played to answering machines)" : "Answering machine recording"}
                </label>
                <select
                  className={`w-full border rounded px-3 py-2 text-sm ${amRecordingMissing ? "border-red-400" : "border-gray-300"}`}
                  value={amRecordingFile}
                  onChange={(e) => setAmRecordingFile(e.target.value)}
                >
                  <option value="">{isDirect ? "— select a voicemail recording —" : "— same as message —"}</option>
                  {(recordings ?? []).map((r) => (
                    <option key={r.filename} value={r.ref}>{r.nickname || r.filename}</option>
                  ))}
                </select>
                {amRecordingMissing && (
                  <p className="text-xs text-red-600 mt-1">Choose the voicemail recording to drop on answering machines.</p>
                )}
                <p className="text-[11px] text-gray-400 mt-1">
                  {isDirect
                    ? "Dropped automatically after the beep when an answering machine is detected."
                    : "Played when an answering machine is detected (separate from the human message)."}
                </p>
                {campaign && (
                  <ReplaceCampaignAudio
                    campaignId={campaign.campaign_id}
                    slot="voicemail"
                    label="Answering machine recording"
                    isRunning={campaign.status === "running"}
                    onSwapped={(ref) => setAmRecordingFile(ref)}
                  />
                )}
              </div>
            </div>
          )}
          {isBroadcast && (
            <div className="mt-2">
              <label className="block text-xs text-gray-500 mb-1">Message recording (played to whoever answers)</label>
              <select
                className={`w-full border rounded px-3 py-2 text-sm ${recordingMissing ? "border-red-400" : "border-gray-300"}`}
                value={recordingFile}
                onChange={(e) => setRecordingFile(e.target.value)}
              >
                <option value="">— select a recording —</option>
                {(recordings ?? []).map((r) => (
                  <option key={r.filename} value={r.ref}>{r.nickname || r.filename}</option>
                ))}
              </select>
              {recordingMissing && (
                <p className="text-xs text-red-600 mt-1">Choose the recording to play when the call is answered.</p>
              )}
              {(recordings ?? []).length === 0 && (
                <p className="text-xs text-amber-700 mt-1">No recordings yet — upload one under Recordings.</p>
              )}
            </div>
          )}
        </div>
        <div className="border-t border-gray-100 pt-3">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Caller ID group</label>
          <select
            className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            value={didGroupId ?? ""}
            onChange={(e) => setDidGroupId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">— select a group you own —</option>
            {(didGroups ?? []).map((g) => (
              <option key={g.did_group_id} value={g.did_group_id}>
                {g.name} ({g.member_count})
              </option>
            ))}
          </select>
          {(didGroups ?? []).length === 0 && (
            <p className="text-xs text-amber-700 mt-1">
              No caller-ID groups yet — create one under Caller ID Groups.
            </p>
          )}
          <p className="text-[11px] text-gray-400 mt-1">
            Numbers rotate randomly with local area-code matching; quarantined numbers and
            per-number volume limits are respected automatically.
          </p>
        </div>
        {isBroadcast && (
          <div>
            <label className="block text-xs text-gray-500 mb-1">Answering machine</label>
            <div className="w-full border border-gray-200 bg-gray-50 rounded px-3 py-2 text-sm text-gray-600">
              Leave a voicemail
            </div>
            {leaveMessage && (
              <div className="mt-2">
                <label className="block text-xs text-gray-500 mb-1">Voicemail recording</label>
                <select
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  value={amRecordingFile}
                  onChange={(e) => setAmRecordingFile(e.target.value)}
                >
                  <option value="">— same as message —</option>
                  {(recordings ?? []).map((r) => (
                    <option key={r.filename} value={r.ref}>{r.nickname || r.filename}</option>
                  ))}
                </select>
                <p className="text-[11px] text-gray-400 mt-1">
                  Left on answering machines. Leave as "same as message" to use the main recording.
                </p>
              </div>
            )}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button
            disabled={!name || recordingMissing || amRecordingMissing || transferMissing || transferNoForward || keysCollide || save.isPending}
            onClick={() => save.mutate()}
            className="flex-1 bg-brand hover:bg-brand-dark text-white rounded py-2 text-sm disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button onClick={onClose} className="flex-1 bg-gray-100 hover:bg-gray-200 rounded py-2 text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function CampaignDidExpansionModal({
  proposals,
  safeDailyCap,
  maxHourlyCap,
  maxDailyCap,
  onClose,
  onApproved,
}: {
  proposals: CampaignExpansionProposal[];
  safeDailyCap: number;
  maxHourlyCap: number;
  maxDailyCap: number;
  onClose: () => void;
  onApproved: (msg: string) => void;
}) {
  const actionable = proposals.filter((p) => p.total_to_buy > 0);
  const [selectedGroupId, setSelectedGroupId] = useState<number>(
    actionable[0]?.group_id ?? proposals[0]?.group_id ?? 0,
  );
  const current =
    actionable.find((p) => p.group_id === selectedGroupId) ??
    actionable[0] ??
    proposals[0];

  const [batchSize, setBatchSize] = useState<number>(current?.total_to_buy ?? 500);
  const [showNpaTable, setShowNpaTable] = useState<boolean>(false);
  const [customQtyByNpa, setCustomQtyByNpa] = useState<Record<string, number>>({});
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const switchPool = (grp: CampaignExpansionProposal) => {
    setSelectedGroupId(grp.group_id);
    setBatchSize(grp.total_to_buy);
    setCustomQtyByNpa({});
    setErrMsg(null);
  };

  const approveMut = useMutation({
    mutationFn: async () => {
      if (!current) throw new Error("No pool selected");
      setErrMsg(null);
      const hasCustom = Object.keys(customQtyByNpa).length > 0;
      const items = hasCustom
        ? current.npa_breakdown
            .map((r) => ({
              npa: r.npa,
              qty: customQtyByNpa[r.npa] ?? r.to_buy,
            }))
            .filter((x) => x.qty > 0)
        : undefined;
      return approveCampaignExpansion({
        group_id: current.group_id,
        max_total: batchSize,
        items,
      });
    },
    onSuccess: (res) => {
      const bought = res.bought_total || res.would_buy_total || 0;
      const modeNote = res.status === "dry_run" ? " (dry-run preview)" : "";
      onApproved(
        `Approved & provisioned ${bought.toLocaleString()} expanded local DIDs into "${res.group_name}" across ${res.by_npa.length} area codes${modeNote}. Inbound forwarding & Free Caller Registry (FCR) auto-registration applied.`,
      );
    },
    onError: (err: unknown) => {
      setErrMsg(campaignActionErrorMessage(err));
    },
  });

  if (!current) return null;

  const effectiveTotalToBuy =
    Object.keys(customQtyByNpa).length > 0
      ? current.npa_breakdown.reduce(
          (acc, r) => acc + (customQtyByNpa[r.npa] ?? r.to_buy),
          0,
        )
      : Math.min(batchSize, Math.max(current.raw_total_shortfall, current.total_to_buy));

  const estCost = (effectiveTotalToBuy * (current.unit_rate || 0.01)).toFixed(2);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-amber-200">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-4 text-white flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🛡️</span>
              <h2 className="text-lg font-bold">
                Approve Caller-ID Pool Expansion for Campaign Volume
              </h2>
            </div>
            <p className="text-xs text-amber-50 mt-1">
              To keep your numbers clean from carrier &ldquo;Spam Likely&rdquo; flags, each DID is
              protected at <strong>{maxHourlyCap} calls/hr</strong> &amp;{" "}
              <strong>{maxDailyCap} calls/day</strong> (target{" "}
              <strong>{safeDailyCap} calls/DID/day</strong>). Approve additional local DIDs below to
              accommodate your campaign volume without throttling.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-xl leading-none px-2"
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto space-y-4 flex-1">
          {actionable.length > 1 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                Select Campaign DID Pool ({actionable.length} pools need expansion)
              </label>
              <div className="flex flex-wrap gap-2">
                {actionable.map((p) => (
                  <button
                    key={p.group_id}
                    onClick={() => switchPool(p)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${
                      p.group_id === current.group_id
                        ? "bg-amber-600 text-white border-amber-600 shadow-sm"
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"
                    }`}
                  >
                    {p.group_name} ({p.owner_username}) — +{p.total_to_buy} DIDs
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Pool Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-[11px] text-gray-500">Caller-ID Pool</div>
              <div className="font-bold text-sm text-gray-900 truncate" title={current.group_name}>
                {current.group_name}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                Owner: <span className="font-medium text-gray-700">{current.owner_username}</span>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-[11px] text-gray-500">Est. Daily Volume</div>
              <div className="font-bold text-sm text-gray-900">
                {current.est_daily_calls.toLocaleString()} calls/day
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {current.total_pending_contacts.toLocaleString()} pending contacts
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="text-[11px] text-gray-500">Current Usable DIDs</div>
              <div className="font-bold text-sm text-gray-900">
                {current.current_usable_dids.toLocaleString()} clean
              </div>
              <div className="text-[11px] text-amber-700 mt-0.5">
                Target: {current.recommended_total_dids.toLocaleString()} DIDs
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[11px] text-amber-800 font-medium">Recommended Batch</div>
              <div className="font-bold text-base text-amber-900">
                +{effectiveTotalToBuy.toLocaleString()} DIDs
              </div>
              <div className="text-[11px] text-amber-700 mt-0.5">
                ${estCost}/{current.billing_unit} ({current.rate_label})
              </div>
            </div>
          </div>

          {/* Campaigns attached to this pool */}
          <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3">
            <div className="text-xs font-semibold text-blue-900 mb-1.5">
              Campaigns Using This Caller-ID Pool ({current.campaigns.length})
            </div>
            <div className="flex flex-wrap gap-2">
              {current.campaigns.map((c) => (
                <span
                  key={c.campaign_id}
                  className="inline-flex items-center gap-1.5 bg-white border border-blue-200 rounded px-2.5 py-1 text-xs text-gray-800"
                >
                  <span className="font-medium">{c.name}</span>
                  <span className={`px-1.5 py-0.2 rounded text-[10px] ${statusBadge(c.status)}`}>
                    {c.status}
                  </span>
                  <span className="text-gray-500">
                    ({c.channel} ch · {c.pending_contacts.toLocaleString()} pending)
                  </span>
                </span>
              ))}
            </div>
          </div>

          {/* Batch Size Selector */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div>
              <div className="text-xs font-semibold text-gray-800">
                How many expanded DIDs would you like to auto-purchase right now?
              </div>
              <div className="text-[11px] text-gray-500">
                Allocated automatically to your highest-volume area codes (with same-state fallback,
                inbound forwarding &amp; FCR registration).
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              {[100, 250, 500, 1000].map((preset) => (
                <button
                  key={preset}
                  type="button"
                  onClick={() => {
                    setCustomQtyByNpa({});
                    setBatchSize(Math.min(preset, Math.max(current.raw_total_shortfall, preset)));
                  }}
                  className={`px-2.5 py-1 rounded text-xs font-medium border ${
                    batchSize === preset && Object.keys(customQtyByNpa).length === 0
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {preset}
                </button>
              ))}
              <input
                type="number"
                min={1}
                max={2000}
                value={effectiveTotalToBuy}
                onChange={(e) => {
                  setCustomQtyByNpa({});
                  setBatchSize(Math.max(1, Math.min(2000, Number(e.target.value) || 1)));
                }}
                className="w-20 border border-gray-300 rounded px-2 py-1 text-xs text-right font-semibold"
              />
            </div>
          </div>

          {/* Expandable Area-Code Breakdown */}
          <div>
            <button
              type="button"
              onClick={() => setShowNpaTable((v) => !v)}
              className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
            >
              <span>{showNpaTable ? "▼" : "▶"}</span>
              <span>
                {showNpaTable ? "Hide" : "View / Customize"} Area Code (NPA) Shortfall Breakdown (
                {current.npas_needing_expansion} area codes)
              </span>
            </button>

            {showNpaTable && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-56 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600 sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Area Code</th>
                      <th className="px-3 py-1.5 text-left">State</th>
                      <th className="px-3 py-1.5 text-right">Est. Calls/Day</th>
                      <th className="px-3 py-1.5 text-right">Usable DIDs</th>
                      <th className="px-3 py-1.5 text-right">Target DIDs</th>
                      <th className="px-3 py-1.5 text-right">Qty to Buy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {current.npa_breakdown.map((row) => {
                      const val = customQtyByNpa[row.npa] ?? row.to_buy;
                      return (
                        <tr key={row.npa}>
                          <td className="px-3 py-1.5 font-mono font-semibold">{row.npa}</td>
                          <td className="px-3 py-1.5 text-gray-600">{row.state}</td>
                          <td className="px-3 py-1.5 text-right">
                            {row.est_daily_calls.toLocaleString()}
                          </td>
                          <td className="px-3 py-1.5 text-right">{row.usable_dids}</td>
                          <td className="px-3 py-1.5 text-right font-medium text-amber-800">
                            {row.target_dids}
                          </td>
                          <td className="px-3 py-1.5 text-right">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={val}
                              onChange={(e) =>
                                setCustomQtyByNpa((prev) => ({
                                  ...prev,
                                  [row.npa]: Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                                }))
                              }
                              className="w-16 border border-gray-300 rounded px-1.5 py-0.5 text-right text-xs"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {errMsg && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
              {errMsg}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3.5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={approveMut.isPending}
            className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-100 text-xs font-medium text-gray-700"
          >
            Remind Me Later
          </button>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-600">
              Est. Cost:{" "}
              <strong className="text-gray-900">
                ${estCost}/{current.billing_unit}
              </strong>{" "}
              ({current.rate_label})
            </span>
            <button
              type="button"
              disabled={approveMut.isPending || effectiveTotalToBuy <= 0}
              onClick={() => approveMut.mutate()}
              className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold shadow-sm disabled:opacity-50"
            >
              {approveMut.isPending
                ? `Ordering ${effectiveTotalToBuy.toLocaleString()} DIDs at Carrier…`
                : `✓ Approve & Auto-Purchase ${effectiveTotalToBuy.toLocaleString()} DIDs`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CampaignsPage() {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["campaigns"],
    queryFn: () => listCampaigns(),
    refetchInterval: 5000, // keep status fresh while campaigns run
  });
  const { data: expansionData } = useQuery({
    queryKey: ["did-expansion-proposals"],
    queryFn: () => getCampaignExpansionProposals(10, 500),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
  const [expansionModalDismissed, setExpansionModalDismissed] = useState(false);
  const [expansionModalForceOpen, setExpansionModalForceOpen] = useState(false);
  const [selected, setSelected] = useState<{ id: number; name: string } | null>(null);
  // F6: per-campaign lead-capture form editor (its own modal; the campaign
  // editor above is already at capacity).
  const [leadFormFor, setLeadFormFor] = useState<{ campaign_id: number; name: string } | null>(null);
  const [pacing, setPacing] = useState<{ id: number; name: string } | null>(null);
  const [editor, setEditor] = useState<Campaign | "new" | null>(null);
  const [editorDraft, setEditorDraft] = useState<CampaignDraftProposal | null>(null);
  const [aiBuilderOpen, setAiBuilderOpen] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const invalidate = () => {
    setActionError(null);
    qc.invalidateQueries({ queryKey: ["campaigns"] });
    qc.invalidateQueries({ queryKey: ["did-expansion-proposals"] });
    qc.invalidateQueries({ queryKey: ["did-groups"] });
  };
  // Surface a refused start (402 insufficient credit, 409 wrong status) instead
  // of swallowing it — the silent failure was the whole bug (2026-08-20).
  const onActionError = (err: unknown) => {
    setActionNotice(null);
    setActionError(campaignActionErrorMessage(err));
  };
  const start = useMutation({ mutationFn: (id: number) => startCampaign(id), onSuccess: invalidate, onError: onActionError });
  const stop = useMutation({ mutationFn: (id: number) => stopCampaign(id), onSuccess: invalidate, onError: onActionError });
  const remove = useMutation({ mutationFn: (id: number) => deleteCampaign(id), onSuccess: invalidate, onError: onActionError });
  const duplicate = useMutation({
    mutationFn: ({ id, name }: { id: number; name?: string }) => duplicateCampaign(id, name),
    onSuccess: (res) => {
      setActionError(null);
      setActionNotice(
        `Duplicated campaign as "${res.name}" (${res.contacts_queued.toLocaleString()} contacts queued).`,
      );
      qc.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: onActionError,
  });
  const resetList = useMutation({
    mutationFn: (id: number) => resetCampaignList(id),
    onSuccess: (res) => {
      setActionError(null);
      const dncNote = res.dnc_suppressed > 0 ? ` (${res.dnc_suppressed.toLocaleString()} newly DNC-suppressed)` : "";
      setActionNotice(
        `Reset list for "${res.campaign_name}": ${res.reset.toLocaleString()} contacts reset to pending, ${res.pending_total.toLocaleString()} ready to dial${dncNote}.`,
      );
      qc.invalidateQueries({ queryKey: ["campaigns"] });
      qc.invalidateQueries({ queryKey: ["campaign-dashboard"] });
    },
    onError: onActionError,
  });

  if (isLoading) return <p className="text-gray-500">Loading campaigns…</p>;
  if (isError) return <p className="text-red-600">Failed to load campaigns: {(error as Error).message}</p>;

  const campaigns: Campaign[] = data ?? [];
  const actionableProposals = (expansionData?.proposals ?? []).filter((p) => p.total_to_buy > 0);
  const showExpansionModal =
    actionableProposals.length > 0 && (!expansionModalDismissed || expansionModalForceOpen);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Campaigns</h1>
        <div className="flex items-center gap-2">
          {actionableProposals.length > 0 && (
            <button
              onClick={() => setExpansionModalForceOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white rounded-lg px-3.5 py-2 text-xs font-bold shadow-sm flex items-center gap-1.5"
            >
              <span>🛡️ Approve Expanded DIDs (+{expansionData?.total_recommended_to_buy ?? 0})</span>
            </button>
          )}
          <button
            onClick={() => setAiBuilderOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-4 py-2 text-sm font-semibold shadow-sm flex items-center gap-1.5"
          >
            <span>✨ + New Campaign (Easy Setup)</span>
          </button>
          <button
            onClick={() => {
              setEditorDraft(null);
              setEditor("new");
            }}
            className="border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 rounded-lg px-3 py-2 text-xs font-medium"
          >
            Classic Form
          </button>
        </div>
      </div>

      {actionableProposals.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3.5 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="text-2xl leading-none mt-0.5">🛡️</span>
            <div>
              <div className="text-sm font-bold text-amber-950">
                Caller-ID Pool Expansion Recommended for Your Campaign Volume
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                To prevent carrier &ldquo;Spam Likely&rdquo; flags (capped at{" "}
                <strong>{expansionData?.did_max_calls_per_hour ?? 4} calls/hr</strong> &amp;{" "}
                <strong>{expansionData?.did_max_calls_per_day ?? 12} calls/day</strong> per DID),{" "}
                {actionableProposals.length === 1 ? (
                  <>
                    pool <strong>{actionableProposals[0].group_name}</strong> needs{" "}
                    <strong>+{actionableProposals[0].total_to_buy.toLocaleString()} local DIDs</strong>{" "}
                    across {actionableProposals[0].npas_needing_expansion} area codes (
                    {actionableProposals[0].est_cost_label}).
                  </>
                ) : (
                  <>
                    <strong>{actionableProposals.length} campaign DID pools</strong> need{" "}
                    <strong>
                      +{(expansionData?.total_recommended_to_buy ?? 0).toLocaleString()} local DIDs
                    </strong>{" "}
                    to sustain your dialing volume safely.
                  </>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setExpansionModalForceOpen(true)}
            className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold shadow-sm whitespace-nowrap"
          >
            Review &amp; Approve Purchase →
          </button>
        </div>
      )}

      {actionNotice && (
        <div
          role="status"
          className="mb-4 flex items-start justify-between gap-3 rounded border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-800"
        >
          <span>{actionNotice}</span>
          <button onClick={() => setActionNotice(null)} className="text-green-600 hover:text-green-800" aria-label="Dismiss notice">×</button>
        </div>
      )}

      {actionError && (
        <div
          role="alert"
          className="mb-4 flex items-start justify-between gap-3 rounded border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <span>{actionError}</span>
          <button onClick={() => setActionError(null)} className="text-red-500 hover:text-red-700" aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Type</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Channels</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {campaigns.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-3 text-gray-500">No campaigns yet.</td></tr>
            ) : (
              campaigns.map((c) => {
                const running = RUNNING_STATES.has(c.status);
                const startable = STARTABLE.has(c.status);
                return (
                  <tr key={c.campaign_id} className={selected?.id === c.campaign_id ? "bg-brand/5" : ""}>
                    <td className="px-4 py-2 font-medium">{c.name}</td>
                    <td className="px-4 py-2 text-gray-600">{serviceLabel(c.service_flag)}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${statusBadge(c.status)}`}>{c.status}</span>
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {c.channel}
                      {c.pacing_enabled && (
                        <span
                          title="Automatic dial pacing is adjusting this cap"
                          className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-800 align-middle"
                        >
                          paced
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                      {running ? (
                        <button
                          onClick={() => stop.mutate(c.campaign_id)}
                          className="px-2 py-1 rounded bg-yellow-500 hover:bg-yellow-600 text-white text-xs"
                        >
                          ■ Stop
                        </button>
                      ) : (
                        <button
                          disabled={!startable}
                          onClick={() => start.mutate(c.campaign_id)}
                          className="px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white text-xs disabled:opacity-40"
                        >
                          ▶ Start
                        </button>
                      )}
                      <button onClick={() => setEditor(c)} className="text-brand hover:underline">Edit</button>
                      <button
                        disabled={duplicate.isPending}
                        onClick={() => duplicate.mutate({ id: c.campaign_id })}
                        className="text-brand hover:underline disabled:opacity-40"
                      >
                        Duplicate
                      </button>
                      <button
                        disabled={running || resetList.isPending}
                        title={running ? "Stop the campaign before resetting its list" : "Reset dialed contacts to pending (DNC numbers stay suppressed)"}
                        onClick={() => {
                          if (
                            confirm(
                              `Reset dial list for "${c.name}"? Dialed and failed contacts will be queued again while DNC numbers stay suppressed.`,
                            )
                          ) {
                            resetList.mutate(c.campaign_id);
                          }
                        }}
                        className="text-brand hover:underline disabled:opacity-40 disabled:no-underline"
                      >
                        Reset list
                      </button>
                      <button
                        onClick={() => setLeadFormFor({ campaign_id: c.campaign_id, name: c.name })}
                        className="text-brand hover:underline"
                      >
                        Lead form
                      </button>
                      <button onClick={() => setSelected({ id: c.campaign_id, name: c.name })} className="text-brand hover:underline">Dashboard</button>
                      <button onClick={() => setPacing({ id: c.campaign_id, name: c.name })} className="text-brand hover:underline">Pacing</button>
                      <button
                        onClick={() => { if (confirm(`Delete campaign "${c.name}"?`)) remove.mutate(c.campaign_id); }}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selected && <CampaignDashboard campaignId={selected.id} name={selected.name} />}

      {leadFormFor && <LeadFormEditor campaign={leadFormFor} onClose={() => setLeadFormFor(null)} />}

      {pacing && <DialPacingPanel campaignId={pacing.id} name={pacing.name} />}

      {editor && (
        <CampaignEditor
          key={editor === "new" ? `new-${editorDraft?.name ?? "blank"}` : editor.campaign_id}
          campaign={editor === "new" ? null : editor}
          initialDraft={editor === "new" ? editorDraft : null}
          onClose={() => {
            setEditor(null);
            setEditorDraft(null);
          }}
          onSaved={() => {
            setEditor(null);
            setEditorDraft(null);
            invalidate();
          }}
          onSwitchToAiBuilder={() => {
            setEditor(null);
            setAiBuilderOpen(true);
          }}
        />
      )}

      {aiBuilderOpen && (
        <CampaignAiBuilderModal
          onClose={() => setAiBuilderOpen(false)}
          onCreated={(campaignId) => {
            setAiBuilderOpen(false);
            setActionNotice(
              `Campaign #${campaignId} created in pending status via Plain-English Builder. Click ▶ Start when you are ready to dial.`,
            );
            invalidate();
          }}
          onOpenClassicForm={(draft) => {
            setAiBuilderOpen(false);
            setEditorDraft(draft);
            setEditor("new");
          }}
        />
      )}

      {showExpansionModal && expansionData && (
        <CampaignDidExpansionModal
          proposals={expansionData.proposals}
          safeDailyCap={expansionData.safe_daily_cap}
          maxHourlyCap={expansionData.did_max_calls_per_hour}
          maxDailyCap={expansionData.did_max_calls_per_day}
          onClose={() => {
            setExpansionModalDismissed(true);
            setExpansionModalForceOpen(false);
          }}
          onApproved={(msg) => {
            setExpansionModalDismissed(true);
            setExpansionModalForceOpen(false);
            setActionNotice(msg);
            invalidate();
          }}
        />
      )}
    </div>
  );
}
